/**
 * Focus management and accessibility utilities
 */

import React, { useEffect, useRef, useCallback } from 'react';

// ==========================================
// Focus Trap Hook
// ==========================================

/**
 * Traps focus within a container element
 */
export function useFocusTrap(isActive: boolean = true) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus first element on mount
    firstElement?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    }

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [isActive]);

  return containerRef;
}

// ==========================================
// Skip to Content Link
// ==========================================

export function SkipToContent() {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-ink focus:text-white focus:rounded-lg focus:outline-none"
    >
      Langsung ke konten utama
    </a>
  );
}

// ==========================================
// Announce to Screen Readers
// ==========================================

let announceElement: HTMLDivElement | null = null;

function getAnnounceElement(): HTMLDivElement {
  if (!announceElement) {
    announceElement = document.createElement('div');
    announceElement.setAttribute('aria-live', 'polite');
    announceElement.setAttribute('aria-atomic', 'true');
    announceElement.className = 'sr-only';
    document.body.appendChild(announceElement);
  }
  return announceElement;
}

/**
 * Announce a message to screen readers
 */
export function announce(message: string, priority: 'polite' | 'assertive' = 'polite') {
  const element = getAnnounceElement();
  element.setAttribute('aria-live', priority);

  // Clear and set message to trigger announcement
  element.textContent = '';
  setTimeout(() => {
    element.textContent = message;
  }, 50);
}

/**
 * Hook for announcing messages to screen readers
 */
export function useAnnounce() {
  return useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    announce(message, priority);
  }, []);
}

// ==========================================
// Keyboard Navigation Hook
// ==========================================

interface UseRovingFocusOptions {
  orientation?: 'horizontal' | 'vertical' | 'both';
  loop?: boolean;
}

/**
 * Roving tabindex for list navigation
 */
export function useRovingFocus<T extends HTMLElement>(
  items: T[],
  options: UseRovingFocusOptions = {}
) {
  const { orientation = 'vertical', loop = true } = options;
  const currentIndex = useRef(0);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const isHorizontal = orientation === 'horizontal' || orientation === 'both';
    const isVertical = orientation === 'vertical' || orientation === 'both';

    let nextIndex = currentIndex.current;

    switch (e.key) {
      case 'ArrowDown':
        if (!isVertical) return;
        e.preventDefault();
        nextIndex = currentIndex.current + 1;
        break;
      case 'ArrowUp':
        if (!isVertical) return;
        e.preventDefault();
        nextIndex = currentIndex.current - 1;
        break;
      case 'ArrowRight':
        if (!isHorizontal) return;
        e.preventDefault();
        nextIndex = currentIndex.current + 1;
        break;
      case 'ArrowLeft':
        if (!isHorizontal) return;
        e.preventDefault();
        nextIndex = currentIndex.current - 1;
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = items.length - 1;
        break;
      default:
        return;
    }

    // Handle looping or clamping
    if (loop) {
      nextIndex = ((nextIndex % items.length) + items.length) % items.length;
    } else {
      nextIndex = Math.max(0, Math.min(nextIndex, items.length - 1));
    }

    currentIndex.current = nextIndex;
    items[nextIndex]?.focus();
  }, [items, orientation, loop]);

  return { handleKeyDown, currentIndex };
}

// ==========================================
// Reduced Motion Hook
// ==========================================

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Check if user prefers reduced motion
 */
export function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const handler = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return prefersReducedMotion;
}

// ==========================================
// Visually Hidden (Screen Reader Only)
// ==========================================

interface VisuallyHiddenProps {
  children: React.ReactNode;
  as?: React.ElementType;
}

export function VisuallyHidden({ children, as: Component = 'span' }: VisuallyHiddenProps) {
  return (
    <Component className="sr-only">
      {children}
    </Component>
  );
}

export default SkipToContent;
